// MyDesk — class detail page. Shows chapters (subjects) each expanded with
// dated lesson cards. Clicking a lesson opens the Learn Now split-screen.
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Play, ChevronDown } from 'lucide-react';
import api from '../studentApi';
import { friendlyError } from '../../../lib/errors';
import { MyDeskClassSkeleton } from '../StudentSkeletons';
import { ST_CARD, ST_BTN, stPrimaryStyle, TINT_PRIMARY, TINT_ACCENT, StPageHeading, EmptyNote } from '../studentUi';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

export default function MyDeskClassPage() {
  const { classId } = useParams();
  const { data = null, isPending: loading, error } = useQuery({
    queryKey: ['student', 'mydesk', 'class', classId],
    queryFn: () => api.get(`/student/mydesk/class/${classId}`),
    meta: { silent: true },
  });
  const err = error ? friendlyError(error, 'Failed to load class') : null;

  if (loading) return <MyDeskClassSkeleton />;
  if (err) return <div className="py-20 text-center text-sm text-red-600">{err}</div>;
  if (!data) return null;
  const { class: cls, chapters, total_lessons } = data;

  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const meta = [cls.grade, cls.subject, cls.teacher_name && `Taught by ${cls.teacher_name}`,
    plural(chapters.length, 'chapter'), plural(total_lessons, 'lesson')].filter(Boolean).join(' · ');

  return (
    <div className="space-y-6" data-testid="mydesk-class-page">
      <div className="space-y-3">
        <Link to="/student/mydesk" className="inline-flex items-center gap-1 text-sm transition-colors hover:underline" style={{ color: 'var(--st-mute)' }} data-testid="mydesk-class-back">
          <ChevronLeft className="w-4 h-4" /> My classes
        </Link>
        <StPageHeading title={cls.name} description={meta} />
      </div>

      {chapters.length === 0 ? (
        <EmptyNote testId="mydesk-class-empty">Your teacher hasn't published anything yet</EmptyNote>
      ) : (
        <div className="space-y-3">
          {chapters.map((ch, i) => <ChapterBlock key={ch.chapter_id} chapter={ch} number={i + 1} openByDefault={i === 0} classId={classId} />)}
        </div>
      )}
    </div>
  );
}

function ChapterBlock({ chapter, number, openByDefault, classId }) {
  const [open, setOpen] = useState(openByDefault);
  return (
    <div className={`${ST_CARD} overflow-hidden`} data-testid={`mydesk-chapter-${chapter.chapter_id}`}>
      <button onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        data-testid={`mydesk-chapter-toggle-${chapter.chapter_id}`}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left transition-colors hover:bg-[color-mix(in_srgb,var(--st-ink)_3%,transparent)]">
        <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-lg font-semibold tabular-nums" style={{ ...TINT_ACCENT, fontFamily: 'var(--st-font-heading)' }} aria-hidden="true">{number}</span>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold truncate">{chapter.chapter_name}</h2>
          <div className="text-sm mt-0.5 tabular-nums" style={{ color: 'var(--st-mute)' }}>{chapter.total} lesson{chapter.total === 1 ? '' : 's'}</div>
        </div>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} style={{ color: 'var(--st-mute)' }} />
      </button>

      {open && (
        <div className="border-t" style={{ borderColor: 'var(--st-border)' }}>
          {chapter.dates.map(d => (
            <div key={d.date} className="px-5 py-4 border-b last:border-b-0" style={{ borderColor: 'var(--st-border)' }}>
              <div className="text-sm mb-3 tabular-nums" style={{ color: 'var(--st-mute)' }}>{fmtDate(d.date)}</div>
              <div className="space-y-2">
                {d.lessons.map(l => <LessonRow key={l.id} l={l} classId={classId} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const KIND_LABEL = { audio: 'Audio lesson', lesson: 'Lesson', topic: 'Lesson' };

function LessonRow({ l }) {
  return (
    <Link to={`/student/mydesk/lesson/${l.id}`}
      data-testid={`mydesk-lesson-${l.id}`}
      className="flex items-center gap-3 p-3 rounded-lg border transition-colors hover:bg-[color-mix(in_srgb,var(--st-ink)_3%,var(--st-card))]"
      style={{ borderColor: 'var(--st-border)', color: 'var(--st-ink)' }}>
      <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={TINT_PRIMARY}>
        <Play className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{l.title}</div>
        <div className="text-sm truncate" style={{ color: 'var(--st-mute)' }}>
          {[KIND_LABEL[l.kind] || l.kind, l.summary].filter(Boolean).join(' · ')}
        </div>
      </div>
      <button
        className={`${ST_BTN} !h-9 shrink-0`}
        style={stPrimaryStyle}
        data-testid={`mydesk-learn-${l.id}`}
        onClick={(e) => { e.preventDefault(); window.location.href = `/student/mydesk/lesson/${l.id}`; }}>
        Learn now
      </button>
    </Link>
  );
}
